import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import api from '../services/api';

const ManageItems = () => {
  const getInitialFormData = () => ({
    name: '',
    code: '',
    unitType: 'items',
    hasCartons: true,
    hasSleeves: false,
    hasUnits: true,
    cartonNote: '',
    unitNote: '',
    unitsPerSleeve: 0,
    unitsPerCarton: 0,
    bossEntryAmount: '',
    isPreparedProduct: false,
    recipe: [] // Array of {componentItemId, quantity}
  });

  const [items, setItems] = useState([]);
  const [showItemForm, setShowItemForm] = useState(false);
  const [editingItemId, setEditingItemId] = useState(null);
  const inlineEditFormRef = useRef(null);
  const inlineEditNameInputRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const selectedBusinessId = new URLSearchParams(location.search).get('businessId');

  // Form state
  const [formData, setFormData] = useState(getInitialFormData());

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (!editingItemId) return;

    const timer = setTimeout(() => {
      inlineEditFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      inlineEditNameInputRef.current?.focus();
      inlineEditNameInputRef.current?.select();
    }, 0);

    return () => clearTimeout(timer);
  }, [editingItemId]);

  const fetchData = async () => {
    try {
      const response = await api.get('/stock-items', {
        params: selectedBusinessId ? { businessId: selectedBusinessId } : undefined
      });
      setItems(response.data.items);
    } catch (err) {
      console.error('Failed to load data:', err);
    }
  };

  const handleCreateItem = async (e) => {
    e.preventDefault();
    try {
      const response = await api.post('/stock-items', {
        ...formData,
        businessId: selectedBusinessId || undefined
      });
      
      // If it's a prepared product, save the recipe
      if (formData.isPreparedProduct && formData.recipe.length > 0) {
        await api.put(`/stock-items/${response.data.itemId}/recipe`, {
          components: formData.recipe
        });
      }
      
      setFormData(getInitialFormData());
      setShowItemForm(false);
      fetchData();
    } catch (err) {
      alert('Failed to create item');
    }
  };

  const handleEditItem = async (item) => {
    // Load recipe if it's a prepared product
    let recipe = [];
    if (item.is_prepared_product) {
      try {
        const response = await api.get(`/stock-items/${item.id}/recipe`);
        recipe = response.data.components.map(c => ({
          componentItemId: c.component_item_id,
          quantity: c.quantity
        }));
      } catch (err) {
        console.error('Failed to load recipe:', err);
      }
    }
    
    setFormData({
      name: item.name,
      code: item.code || '',
      unitType: item.unit_type,
      hasCartons: item.has_cartons,
      hasSleeves: item.has_sleeves,
      hasUnits: item.has_units,
      cartonNote: item.carton_note || '',
      unitNote: item.unit_note || '',
      unitsPerSleeve: item.units_per_sleeve || 0,
      unitsPerCarton: item.units_per_carton || 0,
      bossEntryAmount: item.boss_entry_amount || '',
      isPreparedProduct: item.is_prepared_product || false,
      recipe: recipe
    });
    setEditingItemId(item.id);
    setShowItemForm(false);
  };

  const handleUpdateItem = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/stock-items/${editingItemId}`, {
        ...formData,
        businessId: selectedBusinessId || undefined
      });
      
      // Update recipe if it's a prepared product
      if (formData.isPreparedProduct) {
        await api.put(`/stock-items/${editingItemId}/recipe`, {
          components: formData.recipe
        });
      }
      
      setFormData(getInitialFormData());
      setEditingItemId(null);
      fetchData();
    } catch (err) {
      alert('Failed to update item');
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this item? This will remove it from all areas.')) {
      return;
    }
    
    try {
      await api.delete(`/stock-items/${itemId}`);
      fetchData();
    } catch (err) {
      alert('Failed to delete item');
    }
  };

  useEffect(() => {
    setItems([]);
    fetchData();
  }, [selectedBusinessId]);

  const escapeCsv = (value) => {
    if (value === null || value === undefined) return '';
    const str = String(value);
    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
      return '"' + str.replace(/"/g, '""') + '"';
    }
    return str;
  };

  const handleExportWithSleevesCartons = () => {
    const filtered = items.filter(i => (
      i.has_cartons || i.has_sleeves || (i.units_per_carton && Number(i.units_per_carton) > 0) || (i.units_per_sleeve && Number(i.units_per_sleeve) > 0)
    ));

    if (!filtered.length) {
      alert('No items with sleeve or carton counts to export.');
      return;
    }

    const headers = [
      'id','name','code','unit_type','has_cartons','units_per_carton','carton_note','has_sleeves','units_per_sleeve','has_units','unit_note','boss_entry_amount','is_prepared_product'
    ];

    const rows = [headers.join(',')];

    filtered.forEach(i => {
      const row = [
        escapeCsv(i.id),
        escapeCsv(i.name),
        escapeCsv(i.code),
        escapeCsv(i.unit_type),
        escapeCsv(i.has_cartons),
        escapeCsv(i.units_per_carton),
        escapeCsv(i.carton_note),
        escapeCsv(i.has_sleeves),
        escapeCsv(i.units_per_sleeve),
        escapeCsv(i.has_units),
        escapeCsv(i.unit_note),
        escapeCsv(i.boss_entry_amount),
        escapeCsv(i.is_prepared_product)
      ];
      rows.push(row.join(','));
    });

    const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `items_sleeves_cartons_${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleExportReadable = async () => {
    // export only items that have both cartons and sleeves
    const filtered = items.filter(i => (
      i.has_cartons && i.has_sleeves
    ));

    if (!filtered.length) {
      alert('No items that have both cartons and sleeves to export.');
      return;
    }

    // Prepare rows with only the requested columns (include Type)
    const rows = filtered.map(i => ({
      'Item name': i.name || '',
      'Type': i.unit_type || '',
      'Carton count': i.units_per_carton ?? '',
      'Sleeve count': i.units_per_sleeve ?? ''
    }));

    const headers = ['Item name', 'Type', 'Carton count', 'Sleeve count'];
    const colWidths = headers.map((h, idx) => {
      const maxVal = rows.reduce((max, row) => Math.max(max, String(row[h] ?? '').length), h.length);
      return Math.min(Math.max(maxVal, h.length), 80);
    });

    const pad = (s, w) => {
      const str = String(s ?? '');
      if (str.length >= w) return str;
      return str + ' '.repeat(w - str.length);
    };

    const headerLine = headers.map((h, i) => pad(h, colWidths[i])).join('  ');
    const divider = colWidths.map(w => '-'.repeat(w)).join('  ');
    const lines = [headerLine, divider];

    rows.forEach(row => {
      const line = headers.map((h, i) => pad(row[h] ?? '', colWidths[i])).join('  ');
      lines.push(line);
    });

    const content = lines.join('\r\n');
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `items_sleeves_cartons_${new Date().toISOString().slice(0,10)}.txt`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleCancelEdit = () => {
    setFormData(getInitialFormData());
    setEditingItemId(null);
  };

  const renderItemForm = (isEditMode = false) => (
    <form
      onSubmit={isEditMode ? handleUpdateItem : handleCreateItem}
      className="card mb-3"
      style={{ background: 'var(--pink-light)' }}
      ref={isEditMode ? inlineEditFormRef : undefined}
    >
      <h3 className="mb-2">{isEditMode ? 'Edit Stock Item' : 'New Stock Item'}</h3>
      <div className="form-group">
        <label className="form-label">Item Name *</label>
        <input
          type="text"
          className="form-input"
          value={formData.name}
          onChange={(e) => setFormData({...formData, name: e.target.value})}
          ref={isEditMode ? inlineEditNameInputRef : undefined}
          required
        />
      </div>
      <div className="form-group">
        <label className="form-label">Item Code</label>
        <input
          type="text"
          className="form-input"
          value={formData.code}
          onChange={(e) => setFormData({...formData, code: e.target.value})}
        />
      </div>
      <div className="form-group">
        <label className="form-label">Unit Type *</label>
        <select
          className="form-select"
          value={formData.unitType}
          onChange={(e) => setFormData({...formData, unitType: e.target.value})}
        >
          <option value="items">Items (per piece)</option>
          <option value="litres">Litres</option>
          <option value="kg">Kilograms</option>
          <option value="mL">Millilitres (mL)</option>
        </select>
      </div>
      <div className="form-group">
        <label className="form-label">BOSS Entry Amount (optional)</label>
        <input
          type="number"
          step="0.0001"
          className="form-input"
          placeholder="e.g., 5 for 5kg bags"
          value={formData.bossEntryAmount}
          onChange={(e) => setFormData({...formData, bossEntryAmount: e.target.value})}
        />
        <small style={{ color: 'var(--text-light)', fontSize: '0.8rem', display: 'block', marginTop: '0.25rem' }}>
          For inventory systems that need larger increments (e.g., number of 5kg bags instead of total kg)
        </small>
      </div>
      <div className="form-group">
        <label className="checkbox-label">
          <input
            type="checkbox"
            className="form-checkbox"
            checked={formData.hasCartons}
            onChange={(e) => setFormData({...formData, hasCartons: e.target.checked})}
          />
          Has Cartons
        </label>
      </div>
      {formData.hasCartons && (
        <>
          <div className="form-group">
            <label className="form-label">Carton Note (e.g., "Total Bags")</label>
            <input
              type="text"
              className="form-input"
              value={formData.cartonNote}
              onChange={(e) => setFormData({...formData, cartonNote: e.target.value})}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Units per Carton</label>
            <input
              type="number"
              step="0.01"
              className="form-input"
              value={formData.unitsPerCarton}
              onChange={(e) => setFormData({...formData, unitsPerCarton: e.target.value})}
            />
          </div>
        </>
      )}
      <div className="form-group">
        <label className="checkbox-label">
          <input
            type="checkbox"
            className="form-checkbox"
            checked={formData.hasSleeves}
            onChange={(e) => setFormData({...formData, hasSleeves: e.target.checked})}
          />
          Has Sleeves
        </label>
      </div>
      {formData.hasSleeves && (
        <div className="form-group">
          <label className="form-label">Units per Sleeve</label>
          <input
            type="number"
            step="0.01"
            className="form-input"
            value={formData.unitsPerSleeve}
            onChange={(e) => setFormData({...formData, unitsPerSleeve: e.target.value})}
          />
        </div>
      )}
      <div className="form-group">
        <label className="checkbox-label">
          <input
            type="checkbox"
            className="form-checkbox"
            checked={formData.hasUnits}
            onChange={(e) => setFormData({...formData, hasUnits: e.target.checked})}
          />
          Has Individual Units
        </label>
      </div>
      {formData.hasUnits && (
        <div className="form-group">
          <label className="form-label">Unit Note</label>
          <input
            type="text"
            className="form-input"
            value={formData.unitNote}
            onChange={(e) => setFormData({...formData, unitNote: e.target.value})}
          />
        </div>
      )}
      
      <div className="form-group" style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '2px solid var(--border)' }}>
        <label className="checkbox-label">
          <input
            type="checkbox"
            className="form-checkbox"
            checked={formData.isPreparedProduct}
            onChange={(e) => setFormData({...formData, isPreparedProduct: e.target.checked, recipe: []})}
          />
          This is a prepared product (made from other items)
        </label>
      </div>
      
      {formData.isPreparedProduct && (
        <div style={{ background: 'var(--bg-light)', padding: '1rem', borderRadius: '8px', marginBottom: '1rem' }}>
          <h4 style={{ marginBottom: '0.75rem', fontSize: '1rem' }}>Recipe Components</h4>
          <p style={{ fontSize: '0.85rem', color: 'var(--text-light)', marginBottom: '1rem' }}>
            Add the items needed to make one unit of this product
          </p>
          
          {formData.recipe.map((component, index) => (
            <div key={index} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', alignItems: 'flex-end' }}>
              <div style={{ flex: 2 }}>
                <label className="form-label">Component</label>
                <select
                  className="form-select"
                  value={component.componentItemId || ''}
                  onChange={(e) => {
                    const newRecipe = [...formData.recipe];
                    newRecipe[index].componentItemId = Number(e.target.value);
                    setFormData({...formData, recipe: newRecipe});
                  }}
                >
                  <option value="">Select item...</option>
                  {items.filter(item => !item.is_prepared_product).map(item => (
                    <option key={item.id} value={item.id}>
                      {item.name} ({item.unit_type})
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label className="form-label">Quantity</label>
                <input
                  type="number"
                  step="0.0001"
                  className="form-input"
                  value={component.quantity ?? ''}
                  onChange={(e) => {
                    const newRecipe = [...formData.recipe];
                    newRecipe[index].quantity = e.target.value;
                    setFormData({...formData, recipe: newRecipe});
                  }}
                  placeholder="0.0"
                />
              </div>
              <button
                type="button"
                className="btn btn-error btn-small"
                onClick={() => {
                  const newRecipe = formData.recipe.filter((_, i) => i !== index);
                  setFormData({...formData, recipe: newRecipe});
                }}
                style={{ marginBottom: '0' }}
              >
                Remove
              </button>
            </div>
          ))}
          
          <button
            type="button"
            className="btn btn-secondary btn-small"
            onClick={() => setFormData({...formData, recipe: [...formData.recipe, {componentItemId: '', quantity: 0}]})}
            style={{ marginTop: '0.5rem' }}
          >
            + Add Component
          </button>
        </div>
      )}

      {isEditMode ? (
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button type="submit" className="btn btn-success">
            Update Item
          </button>
          <button type="button" className="btn btn-secondary" onClick={handleCancelEdit}>
            Cancel
          </button>
        </div>
      ) : (
        <button type="submit" className="btn btn-success">
          Create Item
        </button>
      )}
    </form>
  );

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const reorderedItems = Array.from(items);
    const [movedItem] = reorderedItems.splice(result.source.index, 1);
    reorderedItems.splice(result.destination.index, 0, movedItem);

    setItems(reorderedItems);

    // Update order in backend
    try {
      const orderData = reorderedItems.map((item, index) => ({
        id: item.id,
        displayOrder: index
      }));
      await api.post('/stock-items/reorder', { items: orderData });
    } catch (err) {
      console.error('Failed to update order:', err);
      fetchData(); // Revert on error
    }
  };

  return (
    <div className="container">
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Manage Stock Items</h2>
          <button className="btn btn-secondary btn-small" onClick={() => navigate('/')}>
            Back to Dashboard
          </button>
        </div>

        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
          <button 
            className="btn btn-primary"
            onClick={() => {
              if (!showItemForm && editingItemId) handleCancelEdit();
              setShowItemForm(!showItemForm);
            }}
          >
            {showItemForm ? 'Cancel' : 'Create New Item'}
          </button>

          <button
            className="btn btn-secondary"
            onClick={handleExportReadable}
            title="Export items that have sleeve or carton counts (XLSX preferred, CSV fallback)"
          >
            Export (readable)
          </button>
        </div>

        {showItemForm && (
          renderItemForm(false)
        )}

        <h3 className="mb-2">All Stock Items</h3>
        <p style={{ color: 'var(--text-light)', marginBottom: '1rem' }}>
          Drag items to reorder them (affects report display order)
        </p>
        {items.length === 0 ? (
          <div className="text-center" style={{ padding: '2rem', color: 'var(--text-light)' }}>
            No items created yet
          </div>
        ) : (
          <div className="mb-4">
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="all-items">
                {(provided) => (
                  <div {...provided.droppableProps} ref={provided.innerRef}>
                    {items.map((item, index) => (
                      <Draggable key={item.id} draggableId={String(item.id)} index={index}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            className={`list-item ${snapshot.isDragging ? 'list-item-dragging' : ''}`}
                            style={{
                              ...provided.draggableProps.style,
                              flexDirection: 'column',
                              alignItems: 'stretch'
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem' }}>
                              <div className="list-item-content" {...provided.dragHandleProps}>
                                <div className="list-item-title">
                                  {item.name}
                                  {item.is_prepared_product && (
                                    <span style={{ 
                                      marginLeft: '0.5rem', 
                                      fontSize: '0.7rem', 
                                      padding: '0.15rem 0.4rem', 
                                      background: 'var(--pink-dark)', 
                                      color: 'white', 
                                      borderRadius: '4px' 
                                    }}>
                                      PREPARED
                                    </span>
                                  )}
                                </div>
                                <div className="list-item-subtitle">
                                  {item.code && `Code: ${item.code} • `}
                                  Type: {item.unit_type}
                                  {item.boss_entry_amount && ` • BOSS Entry: ${item.boss_entry_amount}`}
                                </div>
                              </div>
                              <div className="list-item-actions">
                                <button
                                  className="btn btn-secondary btn-small"
                                  onClick={() => handleEditItem(item)}
                                  style={{ whiteSpace: 'nowrap', width: 'auto', flex: '0 0 auto' }}
                                >
                                  Edit
                                </button>
                                <button
                                  className="btn btn-danger btn-small"
                                  onClick={() => handleDeleteItem(item.id)}
                                  style={{ whiteSpace: 'nowrap', width: 'auto', flex: '0 0 auto' }}
                                >
                                  Delete
                                </button>
                              </div>
                            </div>

                            {editingItemId === item.id && (
                              <div style={{ marginTop: '1rem' }}>
                                {renderItemForm(true)}
                              </div>
                            )}
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageItems;
